// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
import uuid from 'uuid';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import {
	findParentNodeOfTypeClosestToPos,
	findSelectedNodeOfType,
} from '@atlaskit/editor-prosemirror/utils';

import {
	AGENT_MANAGED_EXTENSION_KEY,
	type AgentManagedExtensionAttributes,
	type AgentManagedObjectAttribute,
} from './agentManagedExtensionPluginType';

export const isAiManagedObjectAttribute = (
	value: unknown,
): value is AgentManagedObjectAttribute => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	const maybeAiManagedObject = value as Partial<AgentManagedObjectAttribute>;
	return (
		typeof maybeAiManagedObject.id === 'string' &&
		typeof maybeAiManagedObject.configVersion === 'string'
	);
};

const parseAiManagedObjectAttribute = (
	value: unknown,
): AgentManagedObjectAttribute | undefined => {
	if (isAiManagedObjectAttribute(value)) {
		return value;
	}

	if (typeof value !== 'string') {
		return undefined;
	}

	try {
		const parsed: unknown = JSON.parse(value);
		return isAiManagedObjectAttribute(parsed) ? parsed : undefined;
	} catch {
		return undefined;
	}
};

export const isAgentManagedExtension = (node: PMNode | null | undefined): boolean => {
	if (!node || node.type.name !== 'bodiedExtension') {
		return false;
	}
	const { extensionKey } = node.attrs as {
		extensionKey?: string;
	};
	return extensionKey === AGENT_MANAGED_EXTENSION_KEY;
};

/**
 * Read the persisted AIMO metadata for an agent-managed block.
 */
export const getNodeAiManagedObject = (
	attrs: AgentManagedExtensionAttributes | undefined,
): AgentManagedObjectAttribute | undefined =>
	parseAiManagedObjectAttribute(
		attrs?.parameters?.macroParams?.aiManagedObject?.value,
	);

/**
 * Find the agent-managed bodied extension at or around the current selection.
 */
export const findAgentManagedExtension = (
	state: EditorState,
	selection?: Selection | null,
): { node: PMNode; pos: number } | undefined => {
	const { bodiedExtension } = state.schema.nodes;
	if (!bodiedExtension) {
		return undefined;
	}

	const sel = selection || state.selection;

	const selected = findSelectedNodeOfType(bodiedExtension)(sel);
	if (selected && isAgentManagedExtension(selected.node)) {
		return { node: selected.node, pos: selected.pos };
	}

	const parent = findParentNodeOfTypeClosestToPos(sel.$from, bodiedExtension);
	if (parent && isAgentManagedExtension(parent.node)) {
		return { node: parent.node, pos: parent.pos };
	}

	return undefined;
};

/** Find an agent-managed bodied extension anywhere in the document by its localId. */
export const findAgentManagedExtensionByLocalId = (
	state: EditorState,
	localId: string | undefined,
): { node: PMNode; pos: number } | undefined => {
	if (!localId) {
		return undefined;
	}

	let found: { node: PMNode; pos: number } | undefined;
	state.doc.descendants((node, pos) => {
		if (isAgentManagedExtension(node) && node.attrs.localId === localId) {
			found = { node, pos };
			return false;
		}
		return true;
	});
	return found;
};

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
	return uuid();
};

/** Generate a stable, locally-unique id for a freshly-inserted block. */
export const generateLocalId = (): string => randomId();
